export const privacyPolicy = {
  title: 'Privacy Policy',
  effectiveDate: 'Effective Date: July 4, 2026 · Last Updated: July 4, 2026',
  sections: [
    {
      heading: '1. Introduction',
      body: `Jotunheims FZCO ("we," "us," or "our"), founded on January 30, 2024, and registered in the IFZA Free Zone, Dubai Silicon Oasis, DDP, Dubai, United Arab Emirates, is committed to protecting the privacy and security of your personal data. We specialize in technology, software development, application development, AI integrations, generative and customized AI, digital marketing, e-commerce, software and network audits, cybersecurity, and renewable energy solutions. This Privacy Policy explains how we collect, use, and safeguard your information.`
    },
    {
      heading: '2. Information We Collect',
      body: `We collect information that identifies or relates to you, including:

Contact Information: Name, email, phone number, and company details.
Technical Data: IP address, browser type, and cookies.
Service & Project Data: Information provided during software audits, AI implementation, and development cycles.`
    },
    {
      heading: '3. Use of Artificial Intelligence',
      body: `Jotunheims FZCO utilizes advanced Artificial Intelligence (AI) and Machine Learning (ML) technologies to enhance our services. Our AI usage includes:

Content & Creative Generation: We use generative AI to assist in the creation of marketing materials, digital advertisements, visual images, and textual content.
Technical Assistance: AI tools are employed to optimize technical support workflows, troubleshoot issues, and provide efficient responses to client inquiries.
Project Development: We integrate AI within our proprietary software and application development workflows to enhance functionality, automation, and performance.
Data Processing: AI may be used to analyze trends or audit data during cybersecurity and network assessments, provided that such use adheres to strict data minimization and confidentiality protocols.`
    },
    {
      heading: '4. How We Use Your Information',
      body: `We process data for the following purposes:

Service Delivery: Providing software development, AI solutions, and digital consulting.
AI-Enhanced Operations: Improving the quality, efficiency, and creativity of our deliverables through AI automation.
Communication: Responding to inquiries and providing updates.
Compliance: Meeting legal obligations under UAE regulations.`
    },
    {
      heading: '5. Data Security & AI Ethics',
      body: `We implement robust technical and organizational measures to protect your data. Regarding AI implementation:

Data Sovereignty: Client data used for custom AI model training or project-specific implementations is processed in isolated, secure environments.
No Cross-Contamination: We ensure distinct separation between client datasets to maintain absolute privacy and intellectual property protection.
Security: We maintain rigorous access controls to ensure that AI tools are used only by authorized personnel and in alignment with our security standards.`
    },
    {
      heading: '6. Data Sharing and Disclosure',
      body: `We do not sell your personal information. Data may be shared with trusted service providers subject to strict confidentiality agreements or when required by UAE law.`
    },
    {
      heading: '7. Your Data Subject Rights',
      body: `Under the UAE PDPL, you have the right to access, rectify, erase, or object to the processing of your data, as well as the right to data portability. To exercise these rights, please contact us at info@jotunheims.com.`
    },
    {
      heading: '8. Changes to This Policy',
      body: `We may update this policy to reflect changes in our AI technologies or regulatory requirements. Continued use of our services constitutes acceptance of these updates.`
    },
    {
      heading: '9. Contact Us',
      body: `Jotunheims FZCO
IFZA, Dubai Silicon Oasis, DDP
Dubai, United Arab Emirates
Email: info@jotunheims.com
WhatsApp: +971504977224
Website: https://www.jotunheims.com

Disclaimer: This document serves as a foundational privacy policy. As you operate in the technology and cybersecurity sector, it is recommended to have this reviewed by legal counsel specializing in UAE Data Protection Law to ensure full compliance with your specific operational scope.`
    }
  ]
};

export const aiDisclaimer = {
  title: 'AI Technology Disclaimer and Liability Limitation',
  effectiveDate: 'Date: July 4, 2026',
  sections: [
    {
      heading: 'Overview',
      body: `At Jotunheims FZCO, we integrate advanced Artificial Intelligence (AI) and machine learning technologies into our development, consulting, and creative workflows to deliver innovative and efficient solutions. To ensure transparency regarding our technological processes, we issue the following disclaimer:`
    },
    {
      heading: '1. Nature of AI-Assisted Services',
      body: `The client acknowledges that Jotunheims FZCO utilizes generative AI, automated tools, and proprietary machine learning models to support project development. By the nature of current AI technology, these systems are probabilistic and may occasionally produce unexpected results, inaccuracies, or technical errors.`
    },
    {
      heading: '2. Error Potential and Commitment to Rectification',
      body: `Jotunheims FZCO employs rigorous human oversight and quality assurance protocols to validate all deliverables. However, the client acknowledges that AI systems—and the resulting software, code, or content—may inherently contain errors, bugs, or anomalies.

Correction Commitment: Jotunheims FZCO is committed to addressing and correcting any identified errors, bugs, or malfunctions within our software or platforms as part of our standard maintenance and support obligations.

Liability Limitation: While we strive for excellence, Jotunheims FZCO does not warrant that any AI-generated component will be entirely error-free or uninterrupted. The client accepts that AI-assisted outputs are subject to validation and testing by the client prior to full-scale production deployment.`
    },
    {
      heading: '3. Human Supervision and Client Validation',
      body: `Although our professional team supervises all AI integrations, the client assumes responsibility for performing final User Acceptance Testing (UAT) and compliance checks. Jotunheims FZCO shall not be held liable for any damages resulting from the failure of the client to adequately test or validate deliverables before their integration into a live production environment.`
    },
    {
      heading: '4. Intellectual Property and Third-Party Tools',
      body: `Jotunheims FZCO utilizes best-in-class tools and robust security measures to protect intellectual property. However, we cannot guarantee absolute immunity from future changes in global AI jurisprudence or copyright laws. Our liability remains limited to the scope of our direct professional obligations, excluding third-party AI service outages or limitations.`
    },
    {
      heading: '5. Governing Law',
      body: `This disclaimer is subject to the laws of the United Arab Emirates. Any disputes regarding the performance or accuracy of AI-integrated deliverables shall be governed by the formal Service Agreement established between the parties.`
    },
    {
      heading: 'Client Acknowledgment',
      body: `By proceeding with the project or accepting deliverables from Jotunheims FZCO, the client acknowledges they have read, understood, and accepted the limitations and risks associated with AI technology as described herein.

Jotunheims FZCO
IFZA, Dubai Silicon Oasis, DDP
Dubai, United Arab Emirates
Email: info@jotunheims.com
WhatsApp: +971504977224
Website: https://www.jotunheims.com`
    }
  ]
};

export const termsAndConditions = {
  title: 'Terms & Conditions',
  effectiveDate: 'Effective Date: July 4, 2026 · Last Updated: July 4, 2026',
  sections: [
    {
      heading: 'Introduction',
      body: `Welcome to Jotunheims FZCO. By accessing our website (https://www.jotunheims.com) and utilizing our services, you agree to comply with and be bound by the following Terms and Conditions. If you do not agree with any part of these terms, please refrain from using our services.`
    },
    {
      heading: '1. Company Information',
      body: `Jotunheims FZCO is a company duly incorporated on January 30, 2024, registered in the IFZA Free Zone, Dubai Silicon Oasis, DDP, Dubai, United Arab Emirates.`
    },
    {
      heading: '2. Scope of Services',
      body: `Jotunheims FZCO provides specialized technology services including, but not limited to:

Software and Application Development.
AI Integrations, Generative AI, and Customized AI Solutions.
Digital Marketing and Automated Advertising.
E-commerce Development.
Software and Network Auditing.
Cybersecurity Consulting.
Renewable Energy Solutions.`
    },
    {
      heading: '3. Use of Artificial Intelligence (AI)',
      body: `Jotunheims FZCO integrates Artificial Intelligence to optimize creative and technical workflows. By using our services, the client acknowledges and agrees that:

We utilize AI tools for the generation of content, digital advertisements, imagery, and technical support.
AI-driven implementations within specific projects remain subject to the terms and scope defined in the individual service agreement signed between the client and Jotunheims FZCO.
Jotunheims FZCO maintains strict ethical and confidentiality protocols, ensuring no cross-contamination of client data during the use of AI models.`
    },
    {
      heading: '4. Intellectual Property Rights',
      body: `All content, design, source code, logos, and materials presented on https://www.jotunheims.com and delivered as part of our professional services are the exclusive property of Jotunheims FZCO, unless otherwise stipulated in a separate written agreement.`
    },
    {
      heading: '5. User Responsibilities',
      body: `The user agrees to:

Refrain from using our website or services for any illegal, unauthorized, or fraudulent purposes.
Not attempt to compromise the security, integrity, or functionality of our systems, networks, or servers.
Provide accurate, current, and complete information when engaging our services.`
    },
    {
      heading: '6. Limitation of Liability',
      body: `Jotunheims FZCO provides its services on an "as is" and "as available" basis. While we strive to maintain the highest standards of quality in our development and security audits, we do not guarantee that our services will be free from occasional technical errors. To the maximum extent permitted by the laws of the United Arab Emirates, Jotunheims FZCO shall not be liable for any indirect, incidental, or consequential damages arising from the use or inability to use our services.`
    },
    {
      heading: '7. Payments and Cancellations',
      body: `Specific terms regarding payment schedules, project deliverables, and cancellation policies will be governed by the formal Service Agreement or Quotation provided to and accepted by the client for each individual project.`
    },
    {
      heading: '8. Governing Law and Jurisdiction',
      body: `These Terms and Conditions shall be governed by and construed in accordance with the laws of the United Arab Emirates. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the competent courts of Dubai.

Dispute Resolution and Arbitration
In the event of any dispute, controversy, or claim arising out of or in connection with these Terms and Conditions, including any question regarding its existence, validity, interpretation, performance, breach, or termination, the parties shall first attempt to resolve the matter amicably through good-faith negotiations within thirty (30) days of the dispute being notified in writing.

If the dispute cannot be resolved amicably, it shall be referred to and finally resolved by arbitration in accordance with the Arbitration Rules of the Dubai International Arbitration Centre (DIAC). The seat, or legal place, of arbitration shall be the Dubai International Financial Centre (DIFC). The language of the arbitration shall be English. The number of arbitrators shall be one (1).`
    },
    {
      heading: '9. Modifications',
      body: `Jotunheims FZCO reserves the right to modify or update these Terms and Conditions at any time. Any changes will be effective immediately upon posting to our website. Your continued use of our services constitutes your acceptance of such changes.`
    },
    {
      heading: '10. Contact Us',
      body: `Jotunheims FZCO
IFZA, Dubai Silicon Oasis, DDP
Dubai, United Arab Emirates
Email: info@jotunheims.com
WhatsApp: +971504977224
Website: https://www.jotunheims.com`
    }
  ]
};